import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { downloadCsv, queryString } from '../api/client'
import { api } from '../api/endpoints'
import { IntermediaryPanel } from '../components/IntermediaryPanel'
import { Card, Empty, ErrorState, Loading } from '../components/StateViews'
import {
  EVENT_LABELS,
  eventBadgeClass,
  eventLabel,
  formatDate,
  formatNumber,
  intermediaryLabel,
} from '../format'
import { useAuth } from '../hooks/useAuth'
import { useUrlFilters } from '../hooks/useUrlFilters'

// The URL keys are the API query parameter names, so a bookmarked view and an
// API call say exactly the same thing.
const INITIAL_FILTERS = {
  from: '',
  to: '',
  type: [] as string[],
  company: '',
  section: '',
  province: '',
  legal_form: '',
}

const SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'UE']
const INPUT_CLASS = 'w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm'

// The central table of the product (§8.2).
export function Movements() {
  const [filters, updateFilters, resetFilters] = useUrlFilters(INITIAL_FILTERS)
  const [openNumber, setOpenNumber] = useState<string | null>(null)
  const { hasRole } = useAuth()

  // Stable, so opening the panel does not re-run its focus handling on every
  // render of this page.
  const closePanel = useCallback(() => setOpenNumber(null), [])

  const movements = useInfiniteQuery({
    queryKey: ['movements', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.movements(filters, pageParam, 100, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })

  const rows = movements.data?.pages.flatMap((page) => page.data) ?? []

  // A mutation rather than a bare promise: the button can say it is working and
  // a refused export shows up instead of dying as an unhandled rejection.
  const exportCsv = useMutation({
    mutationFn: () => {
      const path = hasRole('analyst')
        ? `/api/exports/movements-full.csv${queryString(filters)}`
        : `/api/exports/movements.csv${queryString(filters)}`
      return downloadCsv(path, `movimenti-rui-${new Date().toISOString().slice(0, 10)}.csv`)
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Movimenti</h1>
          <p className="text-sm text-slate-500">
            Le date sono quelle di rilevazione nel registro, non quelle di efficacia.
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportCsv.mutate()}
          disabled={exportCsv.isPending}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          {exportCsv.isPending ? 'Esporto…' : 'Esporta CSV'}
        </button>
      </div>

      {exportCsv.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Esportazione non riuscita: {exportCsv.error.message}
        </p>
      )}

      <Card>
        <div className="grid gap-3 p-4 md:grid-cols-3 lg:grid-cols-6">
          <Field label="Dal">
            <input
              type="date"
              value={filters.from}
              onChange={(event) => updateFilters({ from: event.target.value })}
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Al">
            <input
              type="date"
              value={filters.to}
              onChange={(event) => updateFilters({ to: event.target.value })}
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Tipo">
            <select
              value={filters.type[0] ?? ''}
              onChange={(event) =>
                updateFilters({ type: event.target.value ? [event.target.value] : [] })
              }
              className={INPUT_CLASS}
            >
              <option value="">tutti</option>
              {Object.entries(EVENT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Compagnia">
            <input
              value={filters.company}
              onChange={(event) => updateFilters({ company: event.target.value.toUpperCase() })}
              placeholder="A463S"
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Sezione">
            <select
              value={filters.section}
              onChange={(event) => updateFilters({ section: event.target.value })}
              className={INPUT_CLASS}
            >
              <option value="">tutte</option>
              {SECTIONS.map((section) => (
                <option key={section}>{section}</option>
              ))}
            </select>
          </Field>
          <Field label="Provincia">
            <input
              value={filters.province}
              onChange={(event) =>
                updateFilters({ province: event.target.value.toUpperCase().slice(0, 2) })
              }
              placeholder="RM"
              className={INPUT_CLASS}
            />
          </Field>
        </div>
        <div className="border-t border-slate-100 px-4 py-2 text-right">
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm text-slate-500 underline hover:text-slate-800"
          >
            azzera i filtri
          </button>
        </div>
      </Card>

      <Card>
        {movements.isPending ? (
          <Loading rows={8} />
        ) : movements.isError ? (
          <ErrorState error={movements.error} onRetry={() => void movements.refetch()} />
        ) : rows.length === 0 ? (
          <Empty
            title="Nessun movimento con questi filtri."
            hint="Prova ad allargare l’intervallo di date o a togliere il tipo."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Rilevato il
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Tipo
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Intermediario
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Compagnia
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Mandati residui
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Durata
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Prov.
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((movement) => {
                    const intermediary = movement.intermediary
                    return (
                      <tr
                        key={movement.id}
                        onClick={
                          intermediary
                            ? () => setOpenNumber(intermediary.registration_number)
                            : undefined
                        }
                        className={intermediary ? 'cursor-pointer hover:bg-slate-50' : undefined}
                      >
                        <td className="whitespace-nowrap px-4 py-2 tabular-nums">
                          {formatDate(movement.detected_on)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${eventBadgeClass(movement.type)}`}
                          >
                            {eventLabel(movement.type)}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {/* A real button: a row that only answers the mouse is
                              a row a keyboard cannot reach. */}
                          {intermediary ? (
                            <button
                              type="button"
                              aria-haspopup="dialog"
                              aria-expanded={openNumber === intermediary.registration_number}
                              onClick={(event) => {
                                event.stopPropagation()
                                setOpenNumber(intermediary.registration_number)
                              }}
                              className="text-left underline decoration-dotted decoration-slate-300 hover:decoration-slate-600"
                            >
                              {intermediaryLabel(intermediary)}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {movement.company ? (
                            <Link
                              to={`/companies/${movement.company.code}`}
                              onClick={(event) => event.stopPropagation()}
                              className="underline decoration-slate-300 hover:decoration-slate-600"
                            >
                              {movement.company.name}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatNumber(movement.remaining_mandates)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {movement.mandate_duration_days === null
                            ? '—'
                            : `${formatNumber(movement.mandate_duration_days)} gg`}
                        </td>
                        <td className="px-4 py-2">{intermediary?.province ?? '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>{formatNumber(rows.length)} movimenti caricati</span>
              {movements.hasNextPage && (
                <button
                  type="button"
                  onClick={() => void movements.fetchNextPage()}
                  disabled={movements.isFetchingNextPage}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                >
                  {movements.isFetchingNextPage ? 'carico…' : 'carica altri'}
                </button>
              )}
            </div>
          </>
        )}
      </Card>

      {openNumber && <IntermediaryPanel number={openNumber} onClose={closePanel} />}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}
